import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { ChevronLeft, ChevronRight, Download, X } from 'lucide-react';
import { spring } from '../../tokens';
import type { AttachmentKind } from './attachment-kind';
import { AttachmentFileTypeIcon } from './attachment-filetype-icon';
import { AttachmentPreview } from './attachment-preview';
import type { EventAttachment } from './event-attachments';

export interface AttachmentViewerItem {
  attachment: EventAttachment;
  kind: AttachmentKind;
}

interface AttachmentViewerProps {
  caseId: string;
  items: AttachmentViewerItem[];
  openIndex: number | null;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
  onDownload: (attachment: EventAttachment) => void;
  isDownloading: (attachment: EventAttachment) => boolean;
}

function panelAnimation(shouldReduce: boolean) {
  return shouldReduce
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : {
        initial: { opacity: 0, y: 12 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: 12 },
        transition: spring.gentle,
      };
}

function addKeyboardHandlers(
  enabled: boolean,
  onClose: () => void,
  onNext: () => void,
  onPrev: () => void,
): () => void {
  if (!enabled) {
    return () => {};
  }
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose();
    }
    if (event.key === 'ArrowRight') {
      onNext();
    }
    if (event.key === 'ArrowLeft') {
      onPrev();
    }
  };
  window.addEventListener('keydown', onKeyDown);
  return () => window.removeEventListener('keydown', onKeyDown);
}

function renderViewerModal(
  caseId: string,
  items: AttachmentViewerItem[],
  openIndex: number,
  panelProps: ReturnType<typeof panelAnimation>,
  onClose: () => void,
  onNext: () => void,
  onPrev: () => void,
  onDownload: (attachment: EventAttachment) => void,
  isDownloading: (attachment: EventAttachment) => boolean,
) {
  const current = items[openIndex];
  if (!current) {
    return null;
  }
  const canNavigate = items.length > 1;

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-3 backdrop-blur-md sm:p-4"
      onClick={onClose}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.section
        {...panelProps}
        className="relative flex h-[96vh] w-[97vw] flex-col gap-3 rounded-2xl border border-white/20 bg-surface p-4 shadow-2xl ring-1 ring-black/50"
        onClick={(event) => event.stopPropagation()}
      >
        {renderViewerHeader(
          current.attachment,
          current.kind,
          openIndex,
          items.length,
          onClose,
          onDownload,
          isDownloading,
        )}

        <div className="min-h-0 flex-1 overflow-auto">
          <AttachmentPreview
            caseId={caseId}
            attachment={current.attachment}
            viewer={current.kind.viewer}
            downloading={isDownloading(current.attachment)}
            onDownload={() => onDownload(current.attachment)}
          />
        </div>

        {canNavigate && renderNavigation(onPrev, onNext)}
      </motion.section>
    </motion.div>
  );
}

function renderViewerHeader(
  attachment: EventAttachment,
  kind: AttachmentKind,
  openIndex: number,
  total: number,
  onClose: () => void,
  onDownload: (attachment: EventAttachment) => void,
  isDownloading: (attachment: EventAttachment) => boolean,
) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="flex items-center gap-2 truncate text-sm font-medium text-text-primary">
          {kind.isImage ? null : <AttachmentFileTypeIcon attachment={attachment} />}
          {attachment.fileName ?? 'Vedlegg'}
        </p>
        <p className="text-xs text-text-tertiary">{`${openIndex + 1} av ${total}`}</p>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onDownload(attachment)}
          disabled={!attachment.sourceItemId || isDownloading(attachment)}
          className="inline-flex items-center gap-1.5 rounded-md border border-white/15 px-2.5 py-1.5 text-xs text-text-secondary transition hover:border-white/25 hover:text-text-primary disabled:opacity-60"
        >
          <Download size={13} />
          {isDownloading(attachment) ? 'Laster ned…' : 'Last ned'}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1.5 text-text-tertiary transition hover:bg-white/10 hover:text-text-primary"
          aria-label="Lukk vedleggsvisning"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

function renderNavigation(onPrev: () => void, onNext: () => void) {
  return (
    <div className="flex justify-between">
      <button
        type="button"
        onClick={onPrev}
        className="inline-flex items-center gap-1.5 rounded-md border border-white/15 px-2 py-1.5 text-xs text-text-secondary transition hover:border-white/25 hover:text-text-primary"
      >
        <ChevronLeft size={14} />
        Forrige
      </button>
      <button
        type="button"
        onClick={onNext}
        className="inline-flex items-center gap-1.5 rounded-md border border-white/15 px-2 py-1.5 text-xs text-text-secondary transition hover:border-white/25 hover:text-text-primary"
      >
        Neste
        <ChevronRight size={14} />
      </button>
    </div>
  );
}

export function AttachmentViewer({
  caseId,
  items,
  openIndex,
  onClose,
  onNext,
  onPrev,
  onDownload,
  isDownloading,
}: AttachmentViewerProps) {
  const shouldReduce = useReducedMotion();
  const current = openIndex === null ? undefined : items[openIndex];

  useEffect(() => {
    return addKeyboardHandlers(Boolean(current), onClose, onNext, onPrev);
  }, [current, onClose, onNext, onPrev]);
  const panelProps = panelAnimation(shouldReduce ?? false);

  return createPortal(
    <AnimatePresence>
      {openIndex !== null &&
        renderViewerModal(
          caseId,
          items,
          openIndex,
          panelProps,
          onClose,
          onNext,
          onPrev,
          onDownload,
          isDownloading,
        )}
    </AnimatePresence>,
    document.body,
  );
}
